// Sidebar Error Display
// Shows/hides error messages in the right sidebar when font compilation fails
import { Logger } from './logger';
import { extractFeatureIssuesFromCompilationError } from './feature-error-parser';
import { buildErrorReportPayload } from './state-manager';
const console = new Logger('SidebarErrorDisplay');
export class SidebarErrorDisplay {
    constructor() {
        this.rightSidebar = null;
        this.errorContainer = null;
        this.normalContent = [];
        this.initialized = false;
        this.copyFeedbackResetTimer = null;
        this.stickyErrorVisible = false;
        // Don't initialize here - wait until first use
    }
    initialize() {
        if (this.initialized) {
            return;
        }
        // Get reference to right sidebar
        this.rightSidebar = document.getElementById('glyph-editor-sidebar');
        if (!this.rightSidebar) {
            console.warn('Right sidebar not found yet');
            return;
        }
        // Create error container (hidden by default)
        this.errorContainer = document.createElement('div');
        this.errorContainer.id = 'sidebar-error-display';
        this.errorContainer.style.display = 'none';
        // this.errorContainer.style.padding = '20px';
        this.errorContainer.style.textAlign = 'center';
        this.errorContainer.style.color = 'var(--text-primary)';
        this.rightSidebar.appendChild(this.errorContainer);
        this.initialized = true;
        console.log('[SidebarError] Initialized successfully');
    }
    /**
     * Show error message and hide normal sidebar content
     */
    showError(errorInput, source, options) {
        // Initialize on first use
        this.initialize();
        if (!this.rightSidebar || !this.errorContainer) {
            console.warn('[SidebarError] Cannot show error - sidebar not available yet');
            return;
        }
        const parsedError = this.parseErrorInput(errorInput);
        this.stickyErrorVisible = options?.sticky === true;
        const featureErrorDetails = window.fontInfoManager?.getFeatureCompilationErrorDetails?.(errorInput) || null;
        const hasResolvedFeatureTarget = featureErrorDetails !== null && featureErrorDetails.type !== null;
        const title = source === 'editing'
            ? 'Editing Font Compilation Error'
            : 'Compilation Error';
        const renderedFeatureParsingMessage = featureErrorDetails
            ? `<div style="
                    font-size: 13px;
                    color: var(--text-primary);
                    line-height: 1.5;
                    text-align: center;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    hyphens: auto;
                ">Feature code error: ${this.escapeHtml(featureErrorDetails.message)}</div>`
            : '';
        const renderedMessages = featureErrorDetails
            ? renderedFeatureParsingMessage
            : parsedError.messages.length > 0
                ? parsedError.messages
                    .map((message) => `<div style="
                                    font-size: 12px;
                                    color: var(--text-secondary);
                                    line-height: 1.5;
                                    text-align: left;
                                    white-space: pre-wrap;
                                    user-select: text;
                                    word-wrap: break-word;
                                    overflow-wrap: break-word;
                                    hyphens: auto;
                                    background: var(--background-secondary);
                                    border: 1px solid var(--border-primary);
                                    border-radius: 6px;
                                    padding: 8px 10px;
                                    max-width: 100%;
                                    max-height: 220px;
                                    overflow: auto;
                                ">${this.escapeHtml(message)}</div>`)
                    .join('')
                : `<div style="
                        font-size: 12px;
                        color: var(--text-secondary);
                        line-height: 1.5;
                        text-align: left;
                        white-space: pre-wrap;
                        user-select: text;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        hyphens: auto;
                        background: var(--background-secondary);
                        border: 1px solid var(--border-primary);
                        border-radius: 6px;
                        padding: 8px 10px;
                        max-width: 100%;
                        max-height: 220px;
                        overflow: auto;
                    ">${this.escapeHtml(parsedError.fallback)}</div>`;
        const fallbackLocation = featureErrorDetails
            ? null
            : window.fontInfoManager?.getFeatureCompilationErrorLocation?.(errorInput) || null;
        const renderedLocation = fallbackLocation
            ? `<div style="
                    font-size: 12px;
                    color: var(--text-primary);
                    line-height: 1.5;
                    text-align: center;
                    background: var(--background-secondary);
                    border: 1px solid var(--border-primary);
                    border-radius: 6px;
                    padding: 8px 10px;
                ">Likely in ${this.escapeHtml(fallbackLocation.type)}: <strong>${this.escapeHtml(fallbackLocation.label)}</strong></div>`
            : '';
        const copyableMessage = featureErrorDetails
            ? `Feature code error: ${featureErrorDetails.message}`
            : parsedError.copyText;
        const renderedOpenButton = hasResolvedFeatureTarget
            ? `<button id="sidebar-open-feature-error-btn" style="
                    margin-top: 4px;
                    padding: 6px 10px;
                    border-radius: 6px;
                    border: 1px solid var(--border-primary);
                    background: var(--background-secondary);
                    color: var(--text-primary);
                    font-size: 12px;
                    cursor: pointer;
                ">Open in Features</button>`
            : '';
        const renderedCopyMessageButton = `<button id="sidebar-copy-error-message-btn" style="
                margin-top: 4px;
                padding: 6px 10px;
                border-radius: 6px;
                border: 1px solid var(--border-primary);
                background: var(--background-secondary);
                color: var(--text-primary);
                font-size: 12px;
                cursor: pointer;
            ">Copy Error Message</button>`;
        const renderedCopyButton = `<button id="sidebar-copy-error-report-btn" style="
                margin-top: 4px;
                padding: 6px 10px;
                border-radius: 6px;
                border: 1px solid var(--border-primary);
                background: var(--background-secondary);
                color: var(--text-primary);
                font-size: 12px;
                cursor: pointer;
            ">Copy Error Report</button>`;
        window.fontInfoManager?.showFeatureCompilationError?.(errorInput);
        console.log('[SidebarError] Showing error in sidebar');
        // Store references to normal content
        this.normalContent = Array.from(this.rightSidebar.children);
        // Hide all normal content
        this.normalContent.forEach((child) => {
            if (child !== this.errorContainer) {
                child.style.display = 'none';
            }
        });
        // Create error display
        this.errorContainer.innerHTML = `
            <div style="
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 16px;
                margin-top: 60px;
            ">
                <div style="
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    background-color: var(--background-secondary);
                    border: 2px solid var(--border-primary);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                ">⚠️</div>
                
                <div style="
                    font-weight: 600;
                    font-size: 14px;
                    color: var(--text-primary);
                    text-align: center;
                ">${title}</div>
                ${renderedLocation}
                ${renderedMessages}
                <div style="
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 8px;
                ">
                    ${renderedOpenButton}
                    ${renderedCopyMessageButton}
                    ${renderedCopyButton}
                </div>
                
                <div style="
                    font-size: 12px;
                    color: var(--text-muted);
                    margin-top: 8px;
                    text-align: center;
                    line-height: 1.4;
                ">The font cannot be displayed until the compilation issue is resolved. Check the browser console for detailed error information.</div>
            </div>
        `;
        if (hasResolvedFeatureTarget) {
            const openButton = this.errorContainer.querySelector('#sidebar-open-feature-error-btn');
            openButton?.addEventListener('click', () => {
                window.fontInfoManager?.openFeatureCompilationError?.(errorInput);
            });
        }
        const copyMessageButton = this.errorContainer.querySelector('#sidebar-copy-error-message-btn');
        copyMessageButton?.addEventListener('click', async () => {
            await this.copyErrorMessage(copyableMessage, copyMessageButton);
        });
        const copyButton = this.errorContainer.querySelector('#sidebar-copy-error-report-btn');
        copyButton?.addEventListener('click', async () => {
            await this.copyErrorReport(errorInput, copyButton);
        });
        // Show error container
        this.errorContainer.style.display = 'block';
    }
    /**
     * Copy the current sidebar message text.
     */
    async copyErrorMessage(message, button) {
        const originalLabel = button.textContent || 'Copy Error Message';
        button.disabled = true;
        button.textContent = 'Copying...';
        try {
            await navigator.clipboard.writeText(message);
            this.setCopyButtonFeedback(button, 'Copied', originalLabel);
        }
        catch (error) {
            console.error('[SidebarError] Failed to copy error message:', error);
            this.setCopyButtonFeedback(button, 'Copy failed', originalLabel);
        }
    }
    /**
     * Hide error message and restore normal sidebar content
     */
    hideError(force = false) {
        // Initialize if needed (in case hideError is called first)
        this.initialize();
        if (!this.rightSidebar || !this.errorContainer) {
            console.log('[SidebarError] No error to hide (not initialized yet)');
            return;
        }
        if (this.stickyErrorVisible && !force) {
            console.log('[SidebarError] Keeping sticky error visible');
            return;
        }
        console.log('[SidebarError] Hiding error, restoring normal sidebar');
        window.fontInfoManager?.clearFeatureErrorHighlight?.();
        // Hide error container
        this.errorContainer.style.display = 'none';
        // Restore all normal content
        this.normalContent.forEach((child) => {
            if (child !== this.errorContainer) {
                child.style.display = '';
            }
        });
        this.normalContent = [];
        this.stickyErrorVisible = false;
    }
    /**
     * Copy a runtime-style error report for the current sidebar error.
     */
    async copyErrorReport(errorInput, button) {
        const originalLabel = button.textContent || 'Copy Error Report';
        button.disabled = true;
        button.textContent = 'Copying...';
        try {
            const payload = await buildErrorReportPayload(errorInput, 'editor.compilation.sidebar', 'sidebar-error-display');
            await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
            this.setCopyButtonFeedback(button, 'Copied', originalLabel);
        }
        catch (error) {
            console.error('[SidebarError] Failed to copy error report:', error);
            this.setCopyButtonFeedback(button, 'Copy failed', originalLabel);
        }
    }
    /**
     * Show transient copy feedback and restore the button label.
     */
    setCopyButtonFeedback(button, feedbackLabel, originalLabel) {
        if (this.copyFeedbackResetTimer !== null) {
            window.clearTimeout(this.copyFeedbackResetTimer);
        }
        button.disabled = false;
        button.textContent = feedbackLabel;
        this.copyFeedbackResetTimer = window.setTimeout(() => {
            button.textContent = originalLabel;
            this.copyFeedbackResetTimer = null;
        }, 1500);
    }
    /**
     * Parse error payload from string/Error/object into user-facing messages.
     */
    parseErrorInput(errorInput) {
        const featureIssues = extractFeatureIssuesFromCompilationError(errorInput);
        if (featureIssues.length > 0) {
            const messages = featureIssues.map((issue) => {
                const spanSuffix = issue.start !== undefined && issue.end !== undefined
                    ? ` (span ${issue.start}..${issue.end})`
                    : '';
                return `${issue.category}: ${issue.message + spanSuffix}`;
            });
            return {
                messages,
                fallback: 'Compilation failed.',
                copyText: messages.join('\n')
            };
        }
        const fallbackText = typeof errorInput === 'string'
            ? errorInput
            : errorInput instanceof Error
                ? errorInput.message
                : (() => {
                    try {
                        return JSON.stringify(errorInput);
                    }
                    catch {
                        return String(errorInput);
                    }
                })();
        return {
            messages: [],
            fallback: fallbackText || 'Compilation failed.',
            copyText: fallbackText || 'Compilation failed.'
        };
    }
    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}
// Create global instance
export const sidebarErrorDisplay = new SidebarErrorDisplay();
